/*
 * Mapas PBR do ativo 3D, gerados por procedimento (ver CmaAsset3dView.vue).
 *
 * São azulejados em METROS de mundo, não em UV de malha — `uv` nos TEX_* do
 * consumidor é o lado do azulejo em metros, e é `aplicarUvDeMundo` que projeta
 * a geometria nessa escala. O conjunto é memoizado por resolução: os mesmos
 * canvas servem todas as montagens do dashboard.
 */
/* eslint-disable no-plusplus, no-bitwise, no-continue, no-nested-ternary, no-mixed-operators */
/* eslint-disable no-param-reassign */

const RES_PADRAO = 512;
const RES_ECONOMICA = 256;

const DIVISOR_RES_BAIXA = 2;
const RES_MINIMA_BAIXA = 128;
const ANISOTROPIA_PADRAO = 8;
const SEMENTE_PADRAO = 20260903;

const FAIXA_CHAPA = { minimo: 0.35, maximo: 0.55 };
const FAIXA_METAL = { minimo: 0.45, maximo: 0.75 };
const FAIXA_PORCELANA = { minimo: 0.08, maximo: 0.18 };
const FAIXA_AO = { minimo: 0.76, maximo: 1.0 };

const DERIVA_PINTURA = 0.075;
const DERIVA_METAL = 0.13;
const DERIVA_PORCELANA = 0.045;

const PESO_RELEVO_NO_ALBEDO = 0.35;

const CROMA_PINTURA = 0.030;
const CROMA_PORCELANA = 0.020;

const SUJEIRA_PINTURA = { r: 0.86, g: 0.83, b: 0.78 };
const COBERTURA_SUJEIRA = 0.38;
const PERIODO_SUJEIRA = 8;

const ESTICAMENTO_ESCORRIDO = 4;

const FOSQUEAMENTO_SUJEIRA = 0.45;

const ESTICAMENTO_OCLUSAO = 2;

const OXIDO_METAL = { r: 0.78, g: 0.79, b: 0.82 };
const COBERTURA_OXIDO = 0.34;

const METALNESS_OXIDADA = 0.35;

const FORCA_CHAPA = 0.45;
const FORCA_METAL = 1.7;
const FORCA_SOLDA = 5.0;

const MENISCOS_POR_TILE = 14;
const LARGURA_CORDAO_PADRAO = 0.45;
const ALTURA_CORDAO = 0.86;
const NIVEL_CHAPA = 0.04;
const EXPOENTE_COROA = 0.6;
const PE_POSICAO = 0.66;
const PE_MEIA_LARGURA = 0.34;
const PE_ALTURA = 0.17;
const MODULACAO_MENISCO = 0.20;

function criarPrng(semente) {
  let estado = semente >>> 0;
  return function proximo() {
    estado = (estado + 0x6d2b79f5) >>> 0;
    let t = estado;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function criarGrade(periodoU, periodoV, semente) {
  const sorteio = criarPrng(semente);
  const grade = new Float32Array(periodoU * periodoV);
  for (let i = 0; i < grade.length; i++) grade[i] = sorteio();
  return grade;
}

function suavizar(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function amostrarGrade(grade, periodo, u, v) {
  const fx = u * periodo;
  const fy = v * periodo;
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = suavizar(fx - ix);
  const ty = suavizar(fy - iy);
  const x0 = ((ix % periodo) + periodo) % periodo;
  const y0 = ((iy % periodo) + periodo) % periodo;
  const x1 = (x0 + 1) % periodo;
  const y1 = (y0 + 1) % periodo;
  const linha0 = y0 * periodo;
  const linha1 = y1 * periodo;
  const superior = grade[linha0 + x0] + (grade[linha0 + x1] - grade[linha0 + x0]) * tx;
  const inferior = grade[linha1 + x0] + (grade[linha1 + x1] - grade[linha1 + x0]) * tx;
  return superior + (inferior - superior) * ty;
}

function montarOitavas(resolucao, config) {
  const oitavas = [];
  let periodoU = config.periodo;
  let periodoV = config.periodoV === undefined ? config.periodo : config.periodoV;
  let amplitude = 1;
  let total = 0;
  for (let i = 0; i < config.oitavas && Math.max(periodoU, periodoV) <= resolucao; i++) {
    oitavas.push({
      grade: criarGrade(periodoU, periodoV, config.semente + i * 7919),
      periodoU,
      periodoV,
      amplitude,
    });
    total += amplitude;
    amplitude *= config.ganho;
    periodoU *= 2;
    periodoV *= 2;
  }
  return { oitavas, total: total || 1 };
}

function tabelaEixo(resolucao, periodo) {
  const i0 = new Int32Array(resolucao);
  const i1 = new Int32Array(resolucao);
  const t = new Float32Array(resolucao);
  const escala = periodo / resolucao;
  for (let n = 0; n < resolucao; n++) {
    const f = n * escala;
    const inteiro = Math.floor(f);
    const a = ((inteiro % periodo) + periodo) % periodo;
    i0[n] = a;
    i1[n] = (a + 1) % periodo;
    t[n] = suavizar(f - inteiro);
  }
  return { i0, i1, t };
}

function campoFbm(resolucao, config) {
  const camadas = montarOitavas(resolucao, config);
  const campo = new Float32Array(resolucao * resolucao);
  const inverso = 1 / camadas.total;

  for (let i = 0; i < camadas.oitavas.length; i++) {
    const camada = camadas.oitavas[i];
    const { grade, periodoU, periodoV } = camada;
    const amplitude = camada.amplitude * inverso;
    const eixoU = tabelaEixo(resolucao, periodoU);
    const eixoV = periodoV === periodoU ? eixoU : tabelaEixo(resolucao, periodoV);
    const u0 = eixoU.i0;
    const u1 = eixoU.i1;
    const pesosU = eixoU.t;
    const v0 = eixoV.i0;
    const v1 = eixoV.i1;
    const pesosV = eixoV.t;

    for (let y = 0; y < resolucao; y++) {
      const linha = y * resolucao;
      const linha0 = v0[y] * periodoU;
      const linha1 = v1[y] * periodoU;
      const ty = pesosV[y];
      for (let x = 0; x < resolucao; x++) {
        const x0 = u0[x];
        const x1 = u1[x];
        const tx = pesosU[x];
        const a = grade[linha0 + x0];
        const b = grade[linha0 + x1];
        const c = grade[linha1 + x0];
        const d = grade[linha1 + x1];
        const superior = a + (b - a) * tx;
        const inferior = c + (d - c) * tx;
        campo[linha + x] += (superior + (inferior - superior) * ty) * amplitude;
      }
    }
  }
  return campo;
}

function criarCanvas(largura, altura) {
  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura === undefined ? largura : altura;
  return canvas;
}

function contexto(canvas) {
  return canvas.getContext('2d', { willReadFrequently: true });
}

let suporteACanvas2d = null;

function canvas2dDisponivel() {
  if (suporteACanvas2d !== null) return suporteACanvas2d;
  if (typeof document === 'undefined' || !document.createElement) {
    suporteACanvas2d = false;
    return false;
  }
  try {
    suporteACanvas2d = !!contexto(criarCanvas(1, 1));
  } catch (erro) {
    suporteACanvas2d = false;
  }
  return suporteACanvas2d;
}

function limitar(valor, minimo, maximo) {
  return valor < minimo ? minimo : valor > maximo ? maximo : valor;
}

function identidade(valor) {
  return valor;
}

function linearParaSrgb(valor) {
  return valor <= 0.0031308 ? valor * 12.92 : 1.055 * (valor ** (1 / 2.4)) - 0.055;
}

function bordaMacia(t) {
  const c = limitar(t, 0, 1);
  return c * c * (3 - 2 * c);
}

function normalizarExcursao(campo) {
  let minimo = Infinity;
  let maximo = -Infinity;
  for (let i = 0; i < campo.length; i++) {
    if (campo[i] < minimo) minimo = campo[i];
    if (campo[i] > maximo) maximo = campo[i];
  }
  const faixa = maximo - minimo;
  if (!(faixa > 1e-6)) return campo;
  const escala = 1 / faixa;
  for (let i = 0; i < campo.length; i++) campo[i] = (campo[i] - minimo) * escala;
  return campo;
}

function campoParaCanvas(resolucao, campo, mapear) {
  const canvas = criarCanvas(resolucao);
  const ctx = contexto(canvas);
  const imagem = ctx.createImageData(resolucao, resolucao);
  const dados = imagem.data;
  for (let i = 0; i < campo.length; i++) {
    const nivel = Math.round(limitar(mapear(campo[i], i), 0, 1) * 255);
    const p = i * 4;
    dados[p] = nivel;
    dados[p + 1] = nivel;
    dados[p + 2] = nivel;
    dados[p + 3] = 255;
  }
  ctx.putImageData(imagem, 0, 0);
  return canvas;
}

function alturaParaNormal(canvasAltura, forca) {
  const largura = canvasAltura.width;
  const altura = canvasAltura.height;
  const origem = contexto(canvasAltura).getImageData(0, 0, largura, altura).data;

  const h = new Float32Array(largura * altura);
  for (let i = 0; i < h.length; i++) h[i] = origem[i * 4] / 255;

  const canvas = criarCanvas(largura, altura);
  const ctx = contexto(canvas);
  const destino = ctx.createImageData(largura, altura);
  const saida = destino.data;

  const colunaAnterior = new Int32Array(largura);
  const colunaSeguinte = new Int32Array(largura);
  for (let x = 0; x < largura; x++) {
    colunaAnterior[x] = (x - 1 + largura) % largura;
    colunaSeguinte[x] = (x + 1) % largura;
  }

  for (let y = 0; y < altura; y++) {
    const linhaAnterior = ((y - 1 + altura) % altura) * largura;
    const linhaSeguinte = ((y + 1) % altura) * largura;
    const linha = y * largura;
    for (let x = 0; x < largura; x++) {
      const xAnterior = colunaAnterior[x];
      const xSeguinte = colunaSeguinte[x];

      const gx = (h[linhaAnterior + xSeguinte]
          + 2 * h[linha + xSeguinte] + h[linhaSeguinte + xSeguinte])
        - (h[linhaAnterior + xAnterior] + 2 * h[linha + xAnterior] + h[linhaSeguinte + xAnterior]);
      const gy = (h[linhaSeguinte + xAnterior]
          + 2 * h[linhaSeguinte + x] + h[linhaSeguinte + xSeguinte])
        - (h[linhaAnterior + xAnterior] + 2 * h[linhaAnterior + x] + h[linhaAnterior + xSeguinte]);

      const nx = -gx * forca;
      const ny = gy * forca;
      const comprimento = Math.sqrt(nx * nx + ny * ny + 1);
      const p = (linha + x) * 4;
      saida[p] = Math.round(((nx / comprimento) * 0.5 + 0.5) * 255);
      saida[p + 1] = Math.round(((ny / comprimento) * 0.5 + 0.5) * 255);
      saida[p + 2] = Math.round(((1 / comprimento) * 0.5 + 0.5) * 255);
      saida[p + 3] = 255;
    }
  }
  ctx.putImageData(destino, 0, 0);
  return canvas;
}

function alturaChapa(resolucao, semente) {
  const casca = campoFbm(resolucao, {
    periodo: 24, oitavas: 5, ganho: 0.55, semente,
  });
  const ondulacao = campoFbm(resolucao, {
    periodo: 3, oitavas: 2, ganho: 0.5, semente: semente + 101,
  });
  const campo = new Float32Array(casca.length);
  for (let i = 0; i < campo.length; i++) {
    campo[i] = 0.5 + (casca[i] - 0.5) * 0.85 + (ondulacao[i] - 0.5) * 0.15;
  }
  return campoParaCanvas(resolucao, campo, identidade);
}

function alturaMetal(resolucao, semente) {
  const grao = campoFbm(resolucao, {
    periodo: 12, oitavas: 5, ganho: 0.62, semente: semente + 211,
  });
  const cavidades = campoFbm(resolucao, {
    periodo: 32, oitavas: 2, ganho: 0.5, semente: semente + 337,
  });
  const campo = new Float32Array(grao.length);
  const limiar = 0.30;
  for (let i = 0; i < campo.length; i++) {
    let valor = 0.5 + (grao[i] - 0.5) * 1.15;
    if (cavidades[i] < limiar) {
      const profundidade = (limiar - cavidades[i]) / limiar;
      valor -= profundidade * profundidade * 0.55;
    }
    campo[i] = valor;
  }
  return campoParaCanvas(resolucao, campo, identidade);
}

function ressaltoPe(distancia) {
  const desvio = (distancia - PE_POSICAO) / PE_MEIA_LARGURA;
  if (desvio <= -1 || desvio >= 1) return 0;
  return 0.5 + 0.5 * Math.cos(Math.PI * desvio);
}

function coroaBruta(s) {
  const distancia = Math.abs(s);
  const corpo = (0.5 + 0.5 * Math.cos(Math.PI * s)) ** EXPOENTE_COROA;
  return corpo * (1 - PE_ALTURA) + ressaltoPe(distancia) * PE_ALTURA;
}

const COROA_NO_EIXO = coroaBruta(0);

function perfilCoroa(s) {
  if (Math.abs(s) >= 1) return 0;
  return coroaBruta(s) / COROA_NO_EIXO;
}

function campoSolda(resolucao, semente, larguraCordao) {
  const jitter = criarGrade(MENISCOS_POR_TILE, MENISCOS_POR_TILE, semente + 4211);
  const respingo = campoFbm(resolucao, {
    periodo: 16, oitavas: 3, ganho: 0.5, semente: semente + 733,
  });
  const campo = new Float32Array(resolucao * resolucao);
  const passo = 1 / resolucao;
  const meiaLargura = larguraCordao / 2;

  const perfilU = new Float32Array(resolucao);
  let mediaU = 0;
  for (let x = 0; x < resolucao; x++) {
    const u = x * passo;
    const desvio = (amostrarGrade(jitter, MENISCOS_POR_TILE, u, 0.5) - 0.5) * 0.45;
    const vigor = 0.65 + amostrarGrade(jitter, MENISCOS_POR_TILE, u, 0.13) * 0.35;
    const fase = u * MENISCOS_POR_TILE + desvio;
    const menisco = 0.5 - 0.5 * Math.cos(2 * Math.PI * (fase - Math.floor(fase)));
    perfilU[x] = (menisco ** 0.65) * vigor;
    mediaU += perfilU[x];
  }
  mediaU /= resolucao;

  const alcance = ALTURA_CORDAO - NIVEL_CHAPA;
  for (let y = 0; y < resolucao; y++) {
    const s = (y * passo - 0.5) / meiaLargura;
    const linha = y * resolucao;
    const coroa = perfilCoroa(s);
    if (coroa <= 0) {
      for (let x = 0; x < resolucao; x++) campo[linha + x] = NIVEL_CHAPA;
      continue;
    }
    const pesoOnda = MODULACAO_MENISCO * (coroa ** 1.6);
    const pesoRespingo = 0.05 * coroa;
    for (let x = 0; x < resolucao; x++) {
      const onda = pesoOnda * (perfilU[x] - mediaU);
      const ruido = (respingo[linha + x] - 0.5) * pesoRespingo;
      campo[linha + x] = limitar(NIVEL_CHAPA + alcance * (coroa + onda + ruido), 0, 1);
    }
  }
  return campo;
}

function girarCampo(campo, resolucao) {
  const saida = new Float32Array(campo.length);
  for (let y = 0; y < resolucao; y++) {
    const linha = y * resolucao;
    for (let x = 0; x < resolucao; x++) {
      saida[x * resolucao + (resolucao - 1 - y)] = campo[linha + x];
    }
  }
  return saida;
}

function campoSujeira(resolucao, semente) {
  return normalizarExcursao(campoFbm(resolucao, {
    periodo: PERIODO_SUJEIRA,
    periodoV: PERIODO_SUJEIRA / ESTICAMENTO_ESCORRIDO,
    oitavas: 4,
    ganho: 0.60,
    semente: semente + 3271,
  }));
}

function campoCavidades(resolucao, semente) {
  return normalizarExcursao(campoFbm(resolucao, {
    periodo: 32, oitavas: 2, ganho: 0.50, semente: semente + 337,
  }));
}

function profundidadeSujeira(valor) {
  if (valor >= COBERTURA_SUJEIRA) return 0;
  return bordaMacia((COBERTURA_SUJEIRA - valor) / COBERTURA_SUJEIRA);
}

function profundidadeOxido(valor) {
  if (valor >= COBERTURA_OXIDO) return 0;
  return bordaMacia((COBERTURA_OXIDO - valor) / COBERTURA_OXIDO);
}

function roughnessChapa(resolucao, semente) {
  const manchas = campoFbm(resolucao, {
    periodo: 4, oitavas: 3, ganho: 0.5, semente: semente + 907,
  });
  const micro = campoFbm(resolucao, {
    periodo: 64, oitavas: 2, ganho: 0.5, semente: semente + 1013,
  });
  const escorrido = campoSujeira(resolucao, semente);
  const centro = (FAIXA_CHAPA.minimo + FAIXA_CHAPA.maximo) / 2;
  const amplitude = FAIXA_CHAPA.maximo - FAIXA_CHAPA.minimo;
  return campoParaCanvas(resolucao, manchas, (valor, i) => (
    centro
      + (valor - 0.5) * amplitude * 0.9
      + (micro[i] - 0.5) * amplitude * 0.1
      + profundidadeSujeira(escorrido[i]) * amplitude * FOSQUEAMENTO_SUJEIRA
  ));
}

function roughnessMetal(resolucao, semente) {
  const grosso = campoFbm(resolucao, {
    periodo: 8, oitavas: 4, ganho: 0.58, semente: semente + 1327,
  });
  const poros = campoFbm(resolucao, {
    periodo: 32, oitavas: 2, ganho: 0.5, semente: semente + 337,
  });
  const centro = (FAIXA_METAL.minimo + FAIXA_METAL.maximo) / 2;
  const amplitude = FAIXA_METAL.maximo - FAIXA_METAL.minimo;
  return campoParaCanvas(resolucao, grosso, (valor, i) => {
    const base = centro + (valor - 0.5) * amplitude * 0.8;
    const fosco = poros[i] < 0.30 ? ((0.30 - poros[i]) / 0.30) * amplitude * 0.2 : 0;
    return base + fosco;
  });
}

function roughnessPorcelana(resolucao, semente) {
  const esmalte = campoFbm(resolucao, {
    periodo: 2, oitavas: 2, ganho: 0.45, semente: semente + 1597,
  });
  const micro = campoFbm(resolucao, {
    periodo: 48, oitavas: 2, ganho: 0.5, semente: semente + 1783,
  });
  const centro = (FAIXA_PORCELANA.minimo + FAIXA_PORCELANA.maximo) / 2;
  const amplitude = FAIXA_PORCELANA.maximo - FAIXA_PORCELANA.minimo;
  return campoParaCanvas(resolucao, esmalte, (valor, i) => (
    centro + (valor - 0.5) * amplitude * 0.85 + (micro[i] - 0.5) * amplitude * 0.15
  ));
}

function metalnessMetal(resolucao, semente) {
  const cavidades = campoCavidades(resolucao, semente);
  return campoParaCanvas(resolucao, cavidades, valor => (
    1 - profundidadeOxido(valor) * (1 - METALNESS_OXIDADA)
  ));
}

function mapaAO(resolucao, semente) {
  const suave = campoFbm(resolucao, {
    periodo: 4,
    periodoV: 4 / ESTICAMENTO_OCLUSAO,
    oitavas: 4,
    ganho: 0.55,
    semente: semente + 2069,
  });
  const centro = (FAIXA_AO.minimo + FAIXA_AO.maximo) / 2;
  const amplitude = FAIXA_AO.maximo - FAIXA_AO.minimo;
  return campoParaCanvas(resolucao, suave, valor => centro + (valor - 0.5) * amplitude);
}

function mapaCorParaCanvas(resolucao, calcular) {
  const canvas = criarCanvas(resolucao);
  const ctx = contexto(canvas);
  const imagem = ctx.createImageData(resolucao, resolucao);
  const dados = imagem.data;
  const total = resolucao * resolucao;
  const cor = [0, 0, 0];
  const soma = [0, 0, 0];

  for (let i = 0; i < total; i++) {
    calcular(i, cor);
    const p = i * 4;
    for (let c = 0; c < 3; c++) {
      const valor = limitar(cor[c], 0, 1);
      soma[c] += valor;
      dados[p + c] = Math.round(linearParaSrgb(valor) * 255);
    }
    dados[p + 3] = 255;
  }
  ctx.putImageData(imagem, 0, 0);

  return {
    canvas,
    media: { r: soma[0] / total, g: soma[1] / total, b: soma[2] / total },
  };
}

function albedoChapa(resolucao, semente) {
  const casca = normalizarExcursao(campoFbm(resolucao, {
    periodo: 24, oitavas: 4, ganho: 0.55, semente,
  }));
  const demao = normalizarExcursao(campoFbm(resolucao, {
    periodo: 3, oitavas: 3, ganho: 0.55, semente: semente + 3001,
  }));
  const croma = normalizarExcursao(campoFbm(resolucao, {
    periodo: 5, oitavas: 2, ganho: 0.50, semente: semente + 3119,
  }));
  const sujeira = campoSujeira(resolucao, semente);

  return mapaCorParaCanvas(resolucao, (i, saida) => {
    const deriva = (demao[i] - 0.5) * 2 * DERIVA_PINTURA * (1 - PESO_RELEVO_NO_ALBEDO)
      + (casca[i] - 0.5) * 2 * DERIVA_PINTURA * PESO_RELEVO_NO_ALBEDO;
    const quente = (croma[i] - 0.5) * 2 * CROMA_PINTURA;
    const base = 1 - DERIVA_PINTURA - CROMA_PINTURA + deriva;
    const deposito = profundidadeSujeira(sujeira[i]);
    saida[0] = (base + quente) * (1 - deposito * (1 - SUJEIRA_PINTURA.r));
    saida[1] = base * (1 - deposito * (1 - SUJEIRA_PINTURA.g));
    saida[2] = (base - quente) * (1 - deposito * (1 - SUJEIRA_PINTURA.b));
  });
}

function albedoMetal(resolucao, semente) {
  const grao = normalizarExcursao(campoFbm(resolucao, {
    periodo: 12, oitavas: 4, ganho: 0.62, semente: semente + 211,
  }));
  const usinagem = normalizarExcursao(campoFbm(resolucao, {
    periodo: 4, oitavas: 3, ganho: 0.55, semente: semente + 3407,
  }));
  const cavidades = campoCavidades(resolucao, semente);

  return mapaCorParaCanvas(resolucao, (i, saida) => {
    const deriva = (usinagem[i] - 0.5) * 2 * DERIVA_METAL * (1 - PESO_RELEVO_NO_ALBEDO)
      + (grao[i] - 0.5) * 2 * DERIVA_METAL * PESO_RELEVO_NO_ALBEDO;
    const base = 1 - DERIVA_METAL + deriva;
    const oxido = profundidadeOxido(cavidades[i]);
    saida[0] = base * (1 - oxido * (1 - OXIDO_METAL.r));
    saida[1] = base * (1 - oxido * (1 - OXIDO_METAL.g));
    saida[2] = base * (1 - oxido * (1 - OXIDO_METAL.b));
  });
}

function albedoPorcelana(resolucao, semente) {
  const esmalte = normalizarExcursao(campoFbm(resolucao, {
    periodo: 2, oitavas: 2, ganho: 0.45, semente: semente + 1597,
  }));
  const queima = normalizarExcursao(campoFbm(resolucao, {
    periodo: 7, oitavas: 3, ganho: 0.55, semente: semente + 3533,
  }));

  return mapaCorParaCanvas(resolucao, (i, saida) => {
    const base = 1 - DERIVA_PORCELANA - CROMA_PORCELANA
      + (esmalte[i] - 0.5) * 2 * DERIVA_PORCELANA;
    const quente = (queima[i] - 0.5) * 2 * CROMA_PORCELANA;
    saida[0] = base + quente;
    saida[1] = base;
    saida[2] = base - quente;
  });
}

function empacotar(THREE, canvas, config, encoding) {
  const textura = new THREE.CanvasTexture(canvas);
  textura.wrapS = THREE.RepeatWrapping;
  textura.wrapT = THREE.RepeatWrapping;
  textura.encoding = encoding || THREE.LinearEncoding;
  textura.generateMipmaps = true;
  textura.minFilter = THREE.LinearMipmapLinearFilter;
  textura.magFilter = THREE.LinearFilter;
  textura.anisotropy = config.anisotropia;
  textura.needsUpdate = true;
  return textura;
}

function normalizarOpcoes(opcoes) {
  const dadas = opcoes || {};
  const resolucao = dadas.economico
    ? RES_ECONOMICA
    : ((typeof dadas.resolucao === 'number' && dadas.resolucao > 0) ? dadas.resolucao : RES_PADRAO);
  const anisotropia = typeof dadas.anisotropia === 'number' && dadas.anisotropia > 0
    ? dadas.anisotropia
    : ANISOTROPIA_PADRAO;
  const semente = typeof dadas.semente === 'number' ? dadas.semente >>> 0 : SEMENTE_PADRAO;
  const larguraCordao = typeof dadas.larguraCordao === 'number'
    && dadas.larguraCordao > 0 && dadas.larguraCordao <= 1
    ? dadas.larguraCordao
    : LARGURA_CORDAO_PADRAO;
  return {
    resolucao,
    anisotropia,
    semente,
    larguraCordao,
    comSolda: dadas.comSolda === true,
  };
}

export function aplicarUvDeMundo(geometry) {
  const posicao = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  if (!posicao || !normal) return geometry;

  const uv = new Float32Array(posicao.count * 2);
  for (let i = 0; i < posicao.count; i++) {
    const px = posicao.getX(i);
    const py = posicao.getY(i);
    const pz = posicao.getZ(i);
    const nx = Math.abs(normal.getX(i));
    const ny = Math.abs(normal.getY(i));
    const nz = Math.abs(normal.getZ(i));

    let u;
    let v;
    if (nx >= ny && nx >= nz) {
      u = pz;
      v = py;
    } else if (ny >= nx && ny >= nz) {
      u = px;
      v = pz;
    } else {
      u = px;
      v = py;
    }
    uv[i * 2] = u;
    uv[i * 2 + 1] = v;
  }

  const atributo = new (posicao.constructor)(uv, 2);
  geometry.setAttribute('uv', atributo);
  geometry.setAttribute('uv2', atributo);
  return geometry;
}

export function aplicarIntemperiePorAltura(geometry, elementos, corNaAltura) {
  const posicao = geometry.getAttribute('position');
  if (!posicao) return geometry;

  const cores = new Float32Array(posicao.count * 3);
  const cor = [1, 1, 1];
  for (let i = 0; i < posicao.count; i++) {
    const altura = elementos[1] * posicao.getX(i)
      + elementos[5] * posicao.getY(i)
      + elementos[9] * posicao.getZ(i)
      + elementos[13];
    corNaAltura(altura, cor);
    const [r, g, b] = cor;
    cores[i * 3] = r;
    cores[i * 3 + 1] = g;
    cores[i * 3 + 2] = b;
  }

  geometry.setAttribute('color', new (posicao.constructor)(cores, 3));
  return geometry;
}

export function clonarTextura(THREE, textura, metrosPorRepeticao) {
  if (!textura) return null;
  const t = textura.clone();
  t.needsUpdate = true;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  const r = 1 / Math.max(1e-4, metrosPorRepeticao);
  t.repeat.set(r, r);
  return t;
}

export function aplicarMapaDeCor(THREE, material, textura, media, metrosPorRepeticao) {
  if (!textura || !media) return null;
  const mapa = clonarTextura(THREE, textura, metrosPorRepeticao);
  material.map = mapa;
  material.color.setRGB(
    limitar(material.color.r / Math.max(1e-3, media.r), 0, 1),
    limitar(material.color.g / Math.max(1e-3, media.g), 0, 1),
    limitar(material.color.b / Math.max(1e-3, media.b), 0, 1),
  );
  return mapa;
}

export function remapearRugosidade(THREE, textura, centro, amplitude, metrosPorRepeticao) {
  if (!textura || !textura.image || !textura.image.getContext) return null;
  const fonte = textura.image;
  const largura = fonte.width;
  const altura = fonte.height;
  const dados = contexto(fonte).getImageData(0, 0, largura, altura).data;

  let minimo = 255;
  let maximo = 0;
  let soma = 0;
  let n = 0;
  for (let i = 0; i < dados.length; i += 4) {
    const v = dados[i];
    if (v < minimo) minimo = v;
    if (v > maximo) maximo = v;
    soma += v;
    n++;
  }
  const media = soma / n / 255;
  const excursao = Math.max(maximo / 255 - media, media - minimo / 255) || 1e-6;
  const ganho = amplitude / excursao;

  const alvo = criarCanvas(largura, altura);
  const ctx = contexto(alvo);
  const imagem = ctx.createImageData(largura, altura);
  const saida = imagem.data;
  for (let i = 0; i < dados.length; i += 4) {
    const v = limitar(centro + (dados[i] / 255 - media) * ganho, 0, 1);
    const nivel = Math.round(v * 255);
    saida[i] = nivel;
    saida[i + 1] = nivel;
    saida[i + 2] = nivel;
    saida[i + 3] = 255;
  }
  ctx.putImageData(imagem, 0, 0);

  const nova = empacotar(THREE, alvo, { anisotropia: textura.anisotropy });
  const r = 1 / Math.max(1e-4, metrosPorRepeticao);
  nova.repeat.set(r, r);
  return nova;
}

export function criarTexturas(THREE, opcoes) {
  if (!THREE || !THREE.CanvasTexture) return null;
  if (!canvas2dDisponivel()) return null;

  const config = normalizarOpcoes(opcoes);
  const chave = [
    config.resolucao, config.anisotropia, config.semente,
    config.larguraCordao, config.comSolda,
  ].join('|');
  const emCache = criarTexturas.cache.get(chave);
  if (emCache) {
    emCache.usos += 1;
    return emCache;
  }

  const res = config.resolucao;
  const { semente } = config;

  const escalaForca = res / RES_PADRAO;

  const resBaixa = Math.max(RES_MINIMA_BAIXA, Math.round(res / DIVISOR_RES_BAIXA));
  const corChapa = albedoChapa(resBaixa, semente);
  const corMetal = albedoMetal(resBaixa, semente);
  const corPorcelana = albedoPorcelana(resBaixa, semente);

  const conjunto = {
    chapaNormal: empacotar(THREE, alturaParaNormal(
      alturaChapa(res, semente), FORCA_CHAPA * escalaForca,
    ), config),
    chapaRoughness: empacotar(THREE, roughnessChapa(res, semente), config),
    metalNormal: empacotar(THREE, alturaParaNormal(
      alturaMetal(res, semente), FORCA_METAL * escalaForca,
    ), config),
    metalRoughness: empacotar(THREE, roughnessMetal(res, semente), config),
    porcelanaRoughness: empacotar(THREE, roughnessPorcelana(res, semente), config),
    ruidoAO: empacotar(THREE, mapaAO(res, semente), config),
    chapaAlbedo: empacotar(THREE, corChapa.canvas, config, THREE.sRGBEncoding),
    metalAlbedo: empacotar(THREE, corMetal.canvas, config, THREE.sRGBEncoding),
    porcelanaAlbedo: empacotar(THREE, corPorcelana.canvas, config, THREE.sRGBEncoding),
    metalMetalness: empacotar(THREE, metalnessMetal(resBaixa, semente), config),
    mediasAlbedo: {
      chapa: corChapa.media,
      metal: corMetal.media,
      porcelana: corPorcelana.media,
    },
    usos: 1,
  };

  if (config.comSolda) {
    const campo = campoSolda(res, semente, config.larguraCordao);
    conjunto.soldaNormal = empacotar(THREE, alturaParaNormal(
      campoParaCanvas(res, campo, identidade), FORCA_SOLDA * escalaForca,
    ), config);
    conjunto.soldaNormalVertical = empacotar(THREE, alturaParaNormal(
      campoParaCanvas(res, girarCampo(campo, res), identidade), FORCA_SOLDA * escalaForca,
    ), config);
  }

  conjunto.descartar = function descartar() {
    conjunto.usos -= 1;
    if (conjunto.usos > 0) return;
    Object.keys(conjunto).forEach((nome) => {
      const item = conjunto[nome];
      if (item && typeof item.dispose === 'function') item.dispose();
    });
    criarTexturas.cache.delete(chave);
  };

  criarTexturas.cache.set(chave, conjunto);
  return conjunto;
}

criarTexturas.cache = new Map();

export default criarTexturas;
